<template>
  <section class="flex w-full flex-col items-center justify-center gap-8 overflow-x-hidden rounded-3xl bg-white px-4 py-12 md:px-16 md:py-16">
    <div class="mx-auto flex w-full max-w-[1200px] flex-col gap-12">
      <div class="flex flex-col items-start gap-1">
        <p
          v-if="overlineText"
          class="text-[14px] font-semibold uppercase leading-5 tracking-[1px]"
        >
          {{ overlineText }}
        </p>
        <h2
          class="text-[36px] font-medium leading-[44px] tracking-[-0.02em]"
        >
          {{ headingText }}
        </h2>
      </div>

      <div class="relative overflow-visible">
        <div class="grid grid-cols-1 gap-8 md:grid-cols-4">
          <template
            v-for="(stage, idx) in stages"
            :key="idx"
          >
            <article
              class="relative z-10 flex min-h-[276px] flex-col rounded-xl p-4"
              :class="idx === 0 ? 'border border-gray-200 bg-white' : ''"
              :style="idx === 0 ? {} : { backgroundColor: cardTint }"
            >
              <div class="flex flex-col gap-3">
                <p
                  class="text-[14px] font-semibold uppercase leading-5 tracking-[1px] text-[#262626]"
                >
                  {{ stage.label }}
                </p>
                <p
                  v-if="stage.duration"
                  class="text-[18px] font-semibold leading-7 text-gray-900"
                >
                  <span>{{ durationLabel }}</span>
                  <span class="font-normal">{{ stage.duration }}</span>
                </p>
              </div>

              <ul class="mt-3 flex flex-col gap-3">
                <li
                  v-for="(item, itemIdx) in stage.items"
                  :key="itemIdx"
                  class="flex items-start gap-1"
                >
                  <svg
                    class="mt-0.5 size-5 shrink-0"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M10 3C6.13401 3 3 6.13401 3 10C3 13.866 6.13401 17 10 17C13.866 17 17 13.866 17 10C17 6.13401 13.866 3 10 3ZM13.291 8.29102L9.41602 12.166C9.33594 12.2461 9.23 12.2852 9.125 12.2852C9.02002 12.2852 8.91406 12.2461 8.83398 12.166L6.70898 10.041C6.54902 9.88105 6.54902 9.61895 6.70898 9.45898C6.86895 9.29902 7.13105 9.29902 7.29102 9.45898L9.125 11.292L12.709 7.70801C12.869 7.54805 13.1311 7.54805 13.291 7.70801C13.451 7.86797 13.451 8.13007 13.291 8.29102Z"
                      fill="#262626"
                    />
                  </svg>
                  <span
                    class="text-[14px] leading-5"
                    :class="idx === 0 ? 'text-gray-900' : 'text-gray-700'"
                  >
                    {{ item }}
                  </span>
                </li>
              </ul>
            </article>
          </template>
        </div>

        <img
          v-for="position in desktopArrowPositions"
          :key="position"
          :src="stageArrow"
          alt=""
          aria-hidden="true"
          class="pointer-events-none absolute z-20 hidden h-[86px] w-[86px] -translate-x-1/2 -translate-y-1/2 object-contain md:block"
          :class="position"
        >
      </div>
    </div>

    <div v-if="buttonLabel" class="flex justify-center">
      <a
        :href="buttonUrl || '#'"
        class="inline-flex h-12 items-center justify-center rounded-lg px-5 py-[14px] text-[16px] font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
        :style="{ backgroundImage: brandGradient }"
      >
        {{ buttonLabel }}
      </a>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { themeCardColorFor, BRAND_GRADIENT } from '@/utils/siteThemeColors';
import stageArrow from '@/images/components/arrow.png';

const props = defineProps({
  cardBackgroundColor: { type: String, default: '#fdf2f8' },
  overlineText:        { type: String, default: '' },
  headingText:         { type: String, default: 'Model leczenia' },
  durationLabel:       { type: String, default: 'Czas trwania: ' },

  stage1Label:    { type: String, default: 'Kwalifikacja pary' },
  stage1Duration: { type: String, default: '' },
  stage1Items:    { type: String, default: '' },
  stage2Label:    { type: String, default: 'Etap 1' },
  stage2Duration: { type: String, default: 'ok. 3 msc.' },
  stage2Items:    { type: String, default: '' },
  stage3Label:    { type: String, default: 'Etap 2' },
  stage3Duration: { type: String, default: 'ok. 9 msc.' },
  stage3Items:    { type: String, default: '' },
  stage4Label:    { type: String, default: 'Etap 3' },
  stage4Duration: { type: String, default: 'ok. 12 msc.' },
  stage4Items:    { type: String, default: '' },

  buttonLabel: { type: String, default: 'Umów wizytę' },
  // Stored directly in block data (resource_link); no page-link hydration.
  buttonUrl:   { type: String, default: '' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
});

const cardTint = computed(() => themeCardColorFor(props.site_context));
const brandGradient = BRAND_GRADIENT;

const stages = computed(() => [1, 2, 3, 4].map((i) => ({
  label: props[`stage${i}Label`],
  duration: props[`stage${i}Duration`],
  items: (props[`stage${i}Items`] || '').split('\n').map((s) => s.trim()).filter(Boolean),
})));

const desktopArrowPositions = [
  'left-[calc(50%-11px)] top-0',
  'left-[calc(75%-11px)] top-0',
];
</script>
